<template>
  <div
    v-show="characters"
    :class="
      twMerge(
        'tw:absolute tw:pointer-events-none tw:flex tw:items-center tw:bg-white tw:py-1 tw:gap-1',
        props.placement === 'top-right'
          ? 'tw:top-0 tw:right-0'
          : 'tw:bottom-0 tw:right-0',
        attrs.class as string
      )
    "
    data-testid="character-counter"
  >
    <slot
      :characters="characters"
      :characters-remaining="charactersRemaining"
      :is-exceeded="isExceeded"
      :limit="limit"
      name="before"
    />
    <div
      class="tw:grid tw:place-items-center tw:grid-cols-[auto_auto] tw:gap-1"
      data-testid="character-counter-grid"
    >
      <ProgressPie
        v-show="showProgressPie"
        class="tw:col-start-1 tw:row-start-1"
        :color="barColor"
        data-testid="progress-pie"
        :exceeded-color="resolvedExceededColor"
        :max="limit"
        :options="progressOptions"
        :progress="characters"
        :size="size"
        :track-color="resolvedTrackColor"
      />
      <div
        v-show="
          limit !== Number.MAX_SAFE_INTEGER &&
          charactersRemaining <= warningThreshold
        "
        :class="
          twMerge(
            'exceeded-text tw:row-start-1',
            isTwoColumn
              ? 'tw:col-start-2 tw:text-left'
              : 'tw:col-start-1 tw:text-center'
          )
        "
      >
        <slot
          :characters="characters"
          :characters-remaining="charactersRemaining"
          :is-exceeded="isExceeded"
          :limit="limit"
        >
          <span
            :class="
              twMerge(
                'tw:font-medium tw:whitespace-nowrap',
                charactersRemaining <= 0 && 'tw:text-red-400'
              )
            "
            data-testid="characters-remaining"
          >
            <template v-if="charactersRemaining < 0 && showLimit">
              {{ characters }}/{{ limit }}
            </template>
            <template v-else>{{ charactersRemaining }}</template>
          </span>
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { computed, useAttrs } from "vue";

import { lookupColor } from "shared/helpers/colors";
import { type Maybe } from "shared/types";
import { type Color } from "shared/types/colors";

interface CharacterCounterProps {
  characters?: number;
  exceededColor?: Color;
  limit?: number;
  placement?: "top-right" | "bottom-right";
  showLimit?: boolean;
  size?: number;
  trackColor?: Color;
  warningThreshold?: number;
}

const attrs = useAttrs();

const props = withDefaults(defineProps<CharacterCounterProps>(), {
  characters: 0,
  exceededColor: "ds-red-4",
  limit: Number.MAX_SAFE_INTEGER,
  placement: "bottom-right",
  size: 20,
  trackColor: "ds-denim-2",
  warningThreshold: 20,
});

const progressOptions = computed<{
  animation: boolean;
  cutoutPercentage: number;
  elements: { center: boolean };
}>(() => ({
  animation: false,
  cutoutPercentage: 80,
  elements: { center: false },
}));

const charactersRemaining = computed<number>(
  () => props.limit - props.characters
);

const isExceeded = computed<boolean>(() => props.characters > props.limit);

const resolvedExceededColor = computed<Maybe<string>>(() =>
  lookupColor(props.exceededColor)
);

const resolvedTrackColor = computed<Maybe<string>>(() =>
  lookupColor(props.trackColor)
);

const barColor = computed<Maybe<string>>(() => {
  if (charactersRemaining.value <= props.warningThreshold)
    return lookupColor("ds-orange-4");

  return lookupColor("ds-denim-6");
});

const showProgressPie = computed<boolean>(
  () => charactersRemaining.value > -10 || props.showLimit
);

const exceedFontSize = computed<string>(
  () => `${Math.floor(props.size / 2)}px`
);

const isTwoColumn = computed<boolean>(
  () => props.showLimit && charactersRemaining.value < 0
);
</script>

<style lang="scss" scoped>
.exceeded-text {
  font-size: v-bind(exceedFontSize);
}
</style>
